/**
 * OS09 — A PROVA
 * "No meu Content OS: conteudo virou dado."
 * 10s | 720x900 | SEM AUDIO
 */

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  Easing,
  Sequence,
} from "remotion";
import {CLAMP} from "../../presets/idv";

const T = {
  dark: { bg: "#0D1117", headline: "#FFFFFF", body: "#C8C4C0", muted: "#888888" },
  accent: "#00FF87",
  accentBg: "rgba(0,255,135,0.12)",
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'Fira Code', monospace";
const FONT_SANS = "'Inter', system-ui, sans-serif";

const SZ = { top: 70, sides: 50, bottom: 80 };
const CW = 720 - SZ.sides * 2;

const fadeIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0, 1], { extrapolateRight: CLAMP });
const slideUp = (frame: number, start: number, end: number, dist = 20) =>
  interpolate(frame, [start, end], [dist, 0], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });

const GridPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.03 + Math.sin(frame * 0.02) * 0.005;
  return (
    <div style={{
      position: "absolute", inset: 0,
      backgroundImage: `linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)`,
      backgroundSize: "40px 40px", pointerEvents: "none",
    }} />
  );
};

const CircuitPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.5 + Math.sin(frame * 0.02) * 0.1;
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: `repeating-linear-gradient(0deg, rgba(0,255,135,${opacity * 0.03}) 0px, transparent 1px, transparent 59px, rgba(0,255,135,${opacity * 0.03}) 60px), repeating-linear-gradient(90deg, rgba(0,255,135,${opacity * 0.03}) 0px, transparent 1px, transparent 59px, rgba(0,255,135,${opacity * 0.03}) 60px)`,
      backgroundSize: "60px 60px",
    }} />
  );
};

const SeriesLabel: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", top: 30, left: SZ.sides,
      opacity: fadeIn(frame, 0, 10),
      fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500, letterSpacing: 2,
      textTransform: "uppercase", color: T.dark.muted,
    }}>CONTENT OS</div>
  );
};

const Headline: React.FC<{children: React.ReactNode; size?: number; color?: string; delay?: number}> =
  ({children, size = 48, color = T.dark.headline, delay = 0}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)}px)`,
      fontFamily: FONT_SERIF, fontSize: size, fontWeight: 700, lineHeight: 1.15, color, maxWidth: CW,
    }}>{children}</div>
  );
};

const Body: React.FC<{children: React.ReactNode; color?: string; delay?: number}> = ({children, color = T.dark.body, delay = 15}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)}px)`,
      fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 400, lineHeight: 1.4, color, maxWidth: CW, marginTop: 12,
    }}>{children}</div>
  );
};

type TermLine = { type: "$" | "#" | "text" | "arrow" | "error" | "success"; content: string };

const TerminalCard: React.FC<{lines: TermLine[]; label?: string; startFrame?: number}> = ({lines, label = "diagnostico", startFrame = 30}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, startFrame, startFrame + 10),
      marginTop: 20,
      border: "1px solid rgba(0,255,135,0.15)",
      backgroundColor: "#040810",
      borderRadius: 8, padding: "16px 20px", maxWidth: CW,
    }}>
      <div style={{fontFamily: FONT_MONO, fontSize: 11, color: "#666", marginBottom: 10, letterSpacing: 1.5}}>
        ● {label}
      </div>
      {lines.map((line, i) => {
        const lineColor = line.type === "$" ? "#4ADE80" : line.type === "#" ? "#6B7280" : line.type === "error" ? "#F87171" : line.type === "success" ? "#4ADE80" : line.type === "arrow" ? "#00FF87" : "#9CA3AF";
        const prefix = line.type === "$" ? "$ " : line.type === "#" ? "// " : line.type === "arrow" ? "-> " : "";
        return (
          <div key={i} style={{
            opacity: fadeIn(frame, startFrame + 10 + i * 10, startFrame + 20 + i * 10),
            fontFamily: FONT_MONO, fontSize: 13, color: lineColor, marginBottom: 4, lineHeight: 1.5,
          }}>{prefix}{line.content}</div>
        );
      })}
    </div>
  );
};

const Footer: React.FC<{handle?: string; slide?: string}> = ({handle = "@guilhermeribeiro.me", slide = "9/10"}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", bottom: 40, left: SZ.sides, right: SZ.sides,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      opacity: fadeIn(frame, 40, 50),
    }}>
      <span style={{fontFamily: FONT_SANS, fontSize: 15, fontWeight: 400, color: T.dark.muted}}>{handle}</span>
      <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 500, color: T.dark.muted}}>{slide}</span>
    </div>
  );
};

const SlideNumber: React.FC<{n: string}> = ({n}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", bottom: 70, right: SZ.sides,
      fontFamily: FONT_SERIF, fontSize: 120, fontWeight: 800, color: "#FFFFFF",
      opacity: interpolate(frame, [10, 25], [0, 0.06], {extrapolateRight: CLAMP}),
      lineHeight: 1, userSelect: "none",
    }}>{n}</div>
  );
};

const TERM_LINES: TermLine[] = [
  { type: "$", content: "content.analyze --cross-reference" },
  { type: "text", content: "HEADLINE: curiosidade | autoridade | contraste" },
  { type: "text", content: "GATILHO: transformacao | resultado | dor" },
  { type: "text", content: "FORMATO: carrossel | video curto | estatico" },
  { type: "arrow", content: "output: qual composição converte pra cada perfil" },
  { type: "#", content: "não palpite. dado." },
];

export const OS09AProva: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg}}>
    <Sequence from={0} durationInFrames={300}>
      <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
        <GridPattern />
        <CircuitPattern />
        <SeriesLabel />
        <Headline size={48} delay={5}>
          No meu Content OS: conteudo virou dado.
        </Headline>
        <Body delay={18}>
          Um motor cruza headline x gatilho x formato.
        </Body>
        <TerminalCard lines={TERM_LINES} label="content.analyze" startFrame={30} />
        <SlideNumber n="09" />
        <Footer handle="@guilhermeribeiro.me" slide="9/10" />
      </AbsoluteFill>
    </Sequence>
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
